import React, { FC } from 'react'
import {
  Variant,
  VariantSelector,
  PriceVariantSelector,
  isDigitalGiftCard,
  formatPriceWithoutDecimals,
  formatProductVariants,
  getFormattedVariantSelectorPriceFields,
} from '@moonpig/web-shared-products'
import { useLanguage } from '@moonpig/web-core-stores'
import { useProductContext } from '../../../contexts/product'
import { useVariantSelectorModuleLocaleText } from './VariantSelectorModule.locale'
import { getVariantDescription } from './getVariantDescription'
import { getSelectorTitle } from './getSelectorTitle'
import { ProductDetailsStateActionTypes } from '../../../contexts/product/types'
import { ProductModule, VariantType } from '../types'
import { ProductModuleSection } from '../ProductModuleSection'

export const VariantSelectorModule: FC<ProductModule> = props => {
  const [state, dispatch] = useProductContext()
  const { product, selectedOptions } = state
  const language = useLanguage()
  const localiseText = useVariantSelectorModuleLocaleText()

  const { variants, isLandscape, category } = product

  if (variants.length === 1) return null

  const selectedVariant = variants.find(
    ({ key }) => key === selectedOptions.variantKey,
  ) as Variant

  const variantType = isDigitalGiftCard(product.category.department)
    ? VariantType.price
    : VariantType.size

  const formattedVariants = formatProductVariants({
    variants,
    selectedVariant: selectedVariant ?? variants[0],
    id: product.id,
    language,
  }).map(formattedVariant => ({
    label: formattedVariant.label,
    description: getVariantDescription(formattedVariant, localiseText),
    value: formattedVariant.value,
    ...getFormattedVariantSelectorPriceFields(formattedVariant, language),
  }))

  return (
    <ProductModuleSection {...props}>
      {variantType === VariantType.price ? (
        <PriceVariantSelector
          onChange={(variantSku: string) => {
            dispatch({
              type: ProductDetailsStateActionTypes.SET_SELECTED_VARIANT,
              payload: variantSku,
            })
          }}
          variants={variants
            .filter(v => v.sku !== null)
            .map(v => {
              return {
                sku: v.sku ?? /* istanbul ignore next */ '',
                price: formatPriceWithoutDecimals(
                  Math.abs(v.price.centAmount),
                  v.price.fractionDigits,
                  v.price.currencyCode,
                  1,
                  language,
                ),
              }
            })}
          selectedValue=""
        />
      ) : (
        <VariantSelector
          title={getSelectorTitle(category.id, localiseText)}
          isLandscape={!!isLandscape}
          selectedValue={selectedVariant?.key ?? ''}
          items={formattedVariants}
          onChange={variantKey => {
            dispatch({
              type: ProductDetailsStateActionTypes.SET_SELECTED_VARIANT,
              payload: variantKey,
            })
          }}
        />
      )}
    </ProductModuleSection>
  )
}
